import * as angular from 'angular';
import { TogglePanelCallback } from 'angular/custom/components/toggle-panel.component';
import { Growl } from 'angular/custom/services/growl.service';
import { TransmissionSystemOperatorResource } from 'app/_resources/transmission-system-operator.resource';
import { AuthService } from 'app/_services/auth.service';
import { ErrorHandler } from 'app/_services/error-handler.factory';

class OperatorSettingsSurrenderCtrl implements ng.IController {
    loadingErrorMsg: string;
    balancingGroupPortfolioCodeSelection: BalancingGroupPortfolioCodeSelection;

    settings: SurrenderSettingsDto;
    surrenderEnabled: boolean;
    authenticatedUser: LoginResult;
    togglingSurrender: boolean;
    loading: boolean = false;
    private fallbackContractIdSelection: SelectionPossibility;

    constructor(
        private TransmissionSystemOperator: TransmissionSystemOperatorResource,
        private authService: AuthService,
        private growl: Growl,
        private errorHandler: ErrorHandler,
        private $q: ng.IQService
    ) {}

    $onInit(): void {
        this.loading = true;

        this.authService.userLoginPromise.promise.then((res) => {
            this.authenticatedUser = res;

            this.TransmissionSystemOperator.querySurrenderSettings(
                { id: this.authenticatedUser.referenceId },
                (res: SurrenderSettingsDto) => {
                    this.settings = res;
                    this.surrenderEnabled =
                        this.settings.supportedShortTerm ||
                        this.settings.supportedLongTerm;
                    this.fallbackContractIdSelection =
                        this.settings.contractIdSelection;
                },
                (err: ng.IHttpPromiseCallbackArg<RestError>) => {
                    this.loadingErrorMsg =
                        err && err.data && err.data.message
                            ? err.data.message
                            : 'An unknown error occurred. Please, check back later.';
                }
            ).$promise.finally(() => (this.loading = false));
        });
    }

    deactivateSurrenderSettings(cb: TogglePanelCallback) {
        this.togglingSurrender = true;

        return this.TransmissionSystemOperator.deactivateSurrenderSettings(
            { id: this.authenticatedUser.referenceId },
            this.surrenderEnabled,
            (res: SurrenderSettingsDto) => {
                this.settings = res;
                cb(true);
            },
            (err) => {
                this.errorHandler(err);
                cb(false);
            }
        ).$promise.finally(() => (this.togglingSurrender = false));
    }

    saveEnableSurrenderForShortTerm(): void {
        this.togglingSurrender = true;

        this.TransmissionSystemOperator.saveEnableSurrenderForShortTerm(
            { id: this.authenticatedUser.referenceId },
            this.settings.supportedShortTerm,
            this.successGrowl.bind(this),
            (err: ng.IHttpPromiseCallbackArg<RestError>) => {
                this.settings.supportedShortTerm =
                    !this.settings.supportedShortTerm;
                this.errorHandler(err);
            }
        ).$promise.finally(() => (this.togglingSurrender = false));
    }

    saveEnableSurrenderForLongTerm(): void {
        this.togglingSurrender = true;

        this.TransmissionSystemOperator.saveEnableSurrenderForLongTerm(
            { id: this.authenticatedUser.referenceId },
            this.settings.supportedLongTerm,
            this.successGrowl.bind(this),
            (err: ng.IHttpPromiseCallbackArg<RestError>) => {
                this.settings.supportedLongTerm =
                    !this.settings.supportedLongTerm;
                this.errorHandler(err);
            }
        ).$promise.finally(() => (this.togglingSurrender = false));
    }

    saveContractIdSelection(): void {
        this.togglingSurrender = true;

        this.TransmissionSystemOperator.saveContractIdSelection(
            { id: this.authenticatedUser.referenceId },
            { selectionPossibility: this.settings.contractIdSelection },
            () => {
                this.successGrowl();
                this.fallbackContractIdSelection =
                    this.settings.contractIdSelection;
            },
            (err: ng.IHttpPromiseCallbackArg<RestError>) => {
                this.settings.contractIdSelection =
                    this.fallbackContractIdSelection;
                this.errorHandler(err);
            }
        ).$promise.finally(() => (this.togglingSurrender = false));
    }

    saveReceiveCopyOfConfirmationMail(): void {
        this.togglingSurrender = true;

        this.TransmissionSystemOperator.saveReceiveCopyOfConfirmationMail(
            { id: this.authenticatedUser.referenceId },
            this.settings.receiveCopyOfConfirmationMail,
            this.successGrowl.bind(this),
            (err: ng.IHttpPromiseCallbackArg<RestError>) => {
                this.settings.receiveCopyOfConfirmationMail =
                    !this.settings.receiveCopyOfConfirmationMail;
                this.errorHandler(err);
            }
        ).$promise.finally(() => (this.togglingSurrender = false));
    }

    saveAllowForPendingShippers(): void {
        this.togglingSurrender = true;

        this.TransmissionSystemOperator.saveAllowForPendingShippers(
            { id: this.authenticatedUser.referenceId },
            this.settings.allowForPendingShippers,
            this.successGrowl.bind(this),
            (err: ng.IHttpPromiseCallbackArg<RestError>) => {
                this.settings.allowForPendingShippers =
                    !this.settings.allowForPendingShippers;
                this.errorHandler(err);
            }
        ).$promise.finally(() => (this.togglingSurrender = false));
    }

    saveMinimalLeadTime(): ng.IPromise<any> {
        if (
            (this.settings.minimalLeadTime as any) === '' ||
            this.settings.minimalLeadTime === null ||
            OperatorSettingsSurrenderCtrl.isInteger(
                this.settings.minimalLeadTime
            ) ||
            this.settings.minimalLeadTime <= 0
        ) {
            let err = {
                data: {
                    message: 'Only positive whole numbers allowed.'
                }
            };
            return this.$q.reject(err);
        }

        return this.TransmissionSystemOperator.saveMinimalLeadTime(
            { id: this.authenticatedUser.referenceId },
            this.settings.minimalLeadTime
        ).$promise;
    }

    saveHourForMinimalLeadTime(): ng.IPromise<any> {
        if (
            (this.settings.hourForMinimalLeadTime as any) === '' ||
            this.settings.hourForMinimalLeadTime === null ||
            OperatorSettingsSurrenderCtrl.isInteger(
                this.settings.hourForMinimalLeadTime
            ) ||
            this.settings.hourForMinimalLeadTime < 0 ||
            this.settings.hourForMinimalLeadTime > 23
        ) {
            let err = {
                data: {
                    message: 'You may choose from 0 to 23.'
                }
            };
            return this.$q.reject(err);
        }

        return this.TransmissionSystemOperator.saveHourForMinimalLeadTime(
            { id: this.authenticatedUser.referenceId },
            this.settings.hourForMinimalLeadTime
        ).$promise;
    }

    saveMinuteForMinimalLeadTime(): ng.IPromise<any> {
        if (
            (this.settings.minuteForMinimalLeadTime as any) === '' ||
            this.settings.minuteForMinimalLeadTime === null ||
            OperatorSettingsSurrenderCtrl.isInteger(
                this.settings.minuteForMinimalLeadTime
            ) ||
            this.settings.minuteForMinimalLeadTime < 0 ||
            this.settings.minuteForMinimalLeadTime > 59
        ) {
            let err = {
                data: {
                    message: 'You may choose from 0 to 59'
                }
            };
            return this.$q.reject(err);
        }

        return this.TransmissionSystemOperator.saveMinuteForMinimalLeadTime(
            { id: this.authenticatedUser.referenceId },
            this.settings.minuteForMinimalLeadTime
        ).$promise;
    }

    private static isInteger(number: number) {
        return number % 1 !== 0;
    }

    saveExpectMultipleOfAMonth(): void {
        this.TransmissionSystemOperator.saveExpectMultipleOfAMonth(
            { id: this.authenticatedUser.referenceId },
            this.settings.expectMultipleOfAMonth,
            this.successGrowl.bind(this),
            (err: ng.IHttpPromiseCallbackArg<RestError>) => {
                this.settings.expectMultipleOfAMonth =
                    !this.settings.expectMultipleOfAMonth;
                this.errorHandler(err);
            }
        );
    }

    private successGrowl(): void {
        this.growl.show('Successfully saved.', 'success');
    }
}

angular.module('app').component('pkpOperatorSettingsSurrender', {
    controller: OperatorSettingsSurrenderCtrl,
    template:
        require('app/operator-settings/operator-settings-surrender.component.html'),
    bindings: {
        enabledSettings: '=',
        balancingGroupPortfolioCodeSelection: '<'
    }
});
